'use client';

import { Card } from '@/components/cms/Card';
import { CMSSection } from '@/components/cms/CmsSection';
import { MediaWrapper } from '@/components/cms/MediaWrapper';
import type { CMSContentItem } from '@/lib/cms/types';
import type {
  ContentfulCardFragment,
  ContentGroupFragment,
  ImageWithFocalPoint,
} from '@/lib/contentful/types';
import { filterCollection } from '@/lib/contentful/utils';
import clsx from 'clsx';
import styles from './ThreeColumnScroll.module.scss';

export interface ThreeColumnScrollProps {
  /**
   * Contentful ContentGroup data with Card or MediaWrapper items (legacy format)
   */
  data?: ContentGroupFragment;
  /**
   * Direct items array (adapter format)
   * Takes precedence over data.itemsCollection if provided
   */
  items?: CMSContentItem[];
  /** Optional extra data for items */
  extraData?: Record<string, unknown>;
  /** CMS index for unique data-testid generation */
  cmsIndex?: number;
  /** Optional className */
  className?: string;
}

interface ImageWithFocalPointItem extends ImageWithFocalPoint {
  __typename: 'ImageWithFocalPoint';
}

type ItemData = ContentfulCardFragment | ImageWithFocalPointItem;

/**
 * ThreeColumnScroll - 3-column card/media layout
 *
 * Renders a horizontal scrollable row of items in a 3-column layout.
 * Supports Card and MediaWrapper (ImageWithFocalPoint) content types.
 *
 * Features:
 * - Horizontal scroll on mobile
 * - 3-column grid on desktop
 * - Supports Card and MediaWrapper items
 * - Equal-height columns
 *
 * @example
 * <ThreeColumnScroll
 *   data={contentGroupData}
 *   cmsIndex={0}
 * />
 */
export function ThreeColumnScroll({
  data,
  items: directItems,
  cmsIndex,
  className,
}: ThreeColumnScrollProps) {
  // Prefer direct items (adapter format), fallback to data.itemsCollection (legacy format)
  const items =
    directItems ?? (data?.itemsCollection ? filterCollection(data.itemsCollection) : []);
  const arrayLength = items.length;

  const testId = (element?: string) =>
    cmsIndex !== undefined
      ? element
        ? `threecolumnscroll-${cmsIndex}-${element}`
        : `threecolumnscroll-${cmsIndex}`
      : undefined;

  if (items.length === 0) {
    return null;
  }

  const renderItem = (item: ItemData, index: number) => {
    const typename = item.__typename;
    const sysId = item.sys?.id;

    // Calculate column span based on total items (12 / count for equal distribution)
    const colSpan = Math.floor(12 / arrayLength);

    switch (typename) {
      case 'Card':
        return (
          <div
            key={sysId || index}
            className={clsx(styles.column, styles[`col${colSpan}`])}
            data-testid={testId(`column-${index}`)}
          >
            <Card
              type={(item as ContentfulCardFragment).type as never}
              data={item}
              preload
              cmsIndex={index}
            />
          </div>
        );

      case 'ImageWithFocalPoint': {
        // Cast item to ImageWithFocalPoint and pass directly to MediaWrapper
        return (
          <div
            key={sysId || index}
            className={clsx(styles.column, styles[`col${colSpan}`])}
            data-testid={testId(`column-${index}`)}
          >
            <MediaWrapper media={item as ImageWithFocalPoint} mediaType="Grid" />
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <CMSSection className={clsx(styles.section, className)} data-testid={testId()}>
      <div className={styles.container}>
        <div className={styles.row}>
          {items.map((item, index) => renderItem(item as ItemData, index))}
        </div>
      </div>
    </CMSSection>
  );
}

ThreeColumnScroll.displayName = 'ThreeColumnScroll';
